import _ from 'lodash'

import { ActionHandler, ActionTree } from 'vuex'

import {
  AvailableDate,
  AvailableInterviewDatesQuery,
  Booking,
  BookInterviewMutation,
  BookInterviewMutationVariables,
  CancelInterviewBookingMutation,
  CancelInterviewBookingMutationVariables,
  InterviewBookingsQuery,
  ModuleState,
  RootState,
} from './types'

import { apolloClient } from '@/apollo-provider'

import { Mutation } from './mutations'

import {
  AvailableInterviewDates,
  BookInterview,
  CancelInterviewBooking,
  InterviewBookings,
  InterviewOffers,
} from '@/store/interview/queries.graphql'

export interface WatchAction {
  type: 'watch' | 'interview/bookings/watch'
}

export interface FetchAction {
  type: 'fetch' | 'bookings/fetch' | 'interview/bookings/fetch'
}

export interface BookAction {
  type: 'book' | 'interview/bookings/book'
  date: AvailableDate
}

export interface CancelAction {
  type: 'cancel' | 'interview/bookings/cancel'
  booking: Booking
}

export type Action = BookAction | CancelAction | WatchAction | FetchAction

export const book: ActionHandler<ModuleState, RootState> = (
  { rootGetters },
  { date }: BookAction
) => {
  if (!date!.id) {
    return
  }
  return apolloClient.mutate<BookInterviewMutation, BookInterviewMutationVariables>({
    mutation: BookInterview,
    update(store, { data }) {
      if (!data || !data.created || !data.created!.result!.booking) {
        return
      }
      const cached = store.readQuery<InterviewBookingsQuery>({
        query: InterviewBookings,
      })
      cached!.bookings!.nodes.push(data.created!.result!.booking)
      store.writeQuery({ query: InterviewBookings, data: cached })
    },
    refetchQueries: [{ query: AvailableInterviewDates }, { query: InterviewOffers }],
    variables: {
      caseId: rootGetters.currentCaseId,
      offerId: date!.id,
    },
  })
}

export const cancel: ActionHandler<ModuleState, RootState> = (
  { rootGetters },
  { booking: offer }: CancelAction
) => {
  if (!offer!.id) {
    return
  }
  return apolloClient.mutate<
    CancelInterviewBookingMutation,
    CancelInterviewBookingMutationVariables
  >({
    mutation: CancelInterviewBooking,
    update(store, { data }) {
      const updatedBooking = data!.updated!.result!.booking

      if (!updatedBooking) {
        return
      }

      const cached = store.readQuery<InterviewBookingsQuery>({
        query: InterviewBookings,
      })

      const replaceIndex = _.findIndex(cached!.bookings!.nodes, {
        id: updatedBooking!.id,
      })

      if (replaceIndex > -1) {
        cached!.bookings!.nodes.splice(replaceIndex, 1, updatedBooking)
        store.writeQuery({ query: InterviewBookings, data: cached })
      }
    },
    refetchQueries: [{ query: AvailableInterviewDates }, { query: InterviewOffers }],
    variables: { id: offer!.id },
  })
}

export const watch: ActionHandler<ModuleState, RootState> = ({ commit, state }) => {
  state.subscriptions.dates = apolloClient
    .watchQuery<AvailableInterviewDatesQuery>({
      query: AvailableInterviewDates,
    })
    .subscribe(({ data, errors, loading }) => {
      if (!loading && data) {
        commit<Mutation>({
          dates: data.availableDates!.nodes,
          type: 'setAvailableDates',
        })
      }
    })

  state.subscriptions.bookings = apolloClient
    .watchQuery<InterviewBookingsQuery>({
      query: InterviewBookings,
    })
    .subscribe(({ data, loading }) => {
      if (!loading && data) {
        commit<Mutation>({
          bookings: data.bookings!.nodes,
          type: 'setBookings',
        })
      }
    })
}

export const fetch: ActionHandler<ModuleState, RootState> = ({ commit }) => {
  return Promise.all([
    apolloClient
      .query<AvailableInterviewDatesQuery>({
        fetchPolicy: 'no-cache',
        query: AvailableInterviewDates,
      })
      .then(({ data, errors, loading }) => {
        if (!loading && data) {
          commit<Mutation>({
            dates: data.availableDates!.nodes,
            type: 'setAvailableDates',
          })
        }
      }),
    apolloClient
      .query<InterviewBookingsQuery>({
        fetchPolicy: 'no-cache',
        query: InterviewBookings,
      })
      .then(({ data, loading }) => {
        if (!loading && data) {
          commit<Mutation>({
            bookings: data.bookings!.nodes,
            type: 'setBookings',
          })
        }
      }),
  ])
}

export const actions: ActionTree<ModuleState, RootState> = {
  USER_LOGGED_OUT: {
    root: true,
    handler({ commit }) {
      commit<Mutation>({
        type: 'resetAll',
      })
    },
  },
  USER_SIGNED_IN: {
    root: true,
    handler({ dispatch }) {
      return dispatch<Action>({ type: 'watch' })
    },
  },
  book,
  cancel,
  fetch,
  watch,
}

export default actions
